import { postAIStory, getAIStory, postAICard, patchAIStory } from 'admin/resources';
import { selectActiveOrganizationId } from 'admin/reducers/user/selectors';
import { selectEditableCardId } from 'admin/reducers/card-editor/selectors';
import { selectEditableStoryId } from 'admin/reducers/story-editor/selectors';
import { createThunkAction, handleAPIv2Error } from 'admin/actions/helpers';
import { GET_GENERATED_STORY_CONTENT, GET_GENERATED_CARD_CONTENT } from 'admin/constants/actions';
import { pollWithTimeout } from 'common/utils/polling';
import * as T from './types';

const FAILED_STATUS_MSG = 'Something went wrong, retrying.';
const DEFAULT_STATUS_MSG = 'Sending a request to the Universe...';

const mockConfig = {
	enabled: false,
	response: null as T.GenerateStoryContentResponse | null,
};

if (mockConfig.enabled) {
	const mock = await import('./mock.json');
	mockConfig.response = mock.default as unknown as T.GenerateStoryContentResponse;
}

export const generateStoryContent = createThunkAction<T.GenerateStoryContentResult, T.GenerateStoryContentParams>({
	type: GET_GENERATED_STORY_CONTENT,
	payloadCreator: async ({ onStatusChange, ...payload }, { getState }) => {
		const organizationId = selectActiveOrganizationId(getState());
		let response: { success: boolean; result: T.GenerateStoryContentResponse | null };
		let body: { id: string; jobId: string };

		if (mockConfig.enabled && mockConfig.response) {
			body = { id: mockConfig.response.id, jobId: mockConfig.response.jobs[0].id };
			response = { result: mockConfig.response, success: true };
		} else {
			const postResponse = await postAIStory.params({ organizationId }).send(payload);
			body = postResponse.body as { id: string; jobId: string };

			const pollResult = await pollWithTimeout({
				checkStatus: async () => {
					const { body: responseBody } = await getAIStory
						.params({ organizationId, generatorId: body.id })
						.send({ jobId: body.jobId });
					return responseBody as T.GenerateStoryContentResponse;
				},
				shouldContinue: data => {
					const job = data.jobs.find(j => j.id === body.jobId);
					return job?.status !== T.GenerateStoryStatus.completed && job?.status !== 'failed';
				},
				isFailed: data => {
					const job = data.jobs.find(j => j.id === body.jobId);
					const statusMessages: string[] = job?.statuses ? [...job.statuses] : [];
					const currentStatus = statusMessages[statusMessages.length - 1];
					const failedStatusesLen = statusMessages.filter(m => m === FAILED_STATUS_MSG).length;
					return failedStatusesLen % 2 === 0 && currentStatus === FAILED_STATUS_MSG;
				},
				getStatusMessage: data => {
					const job = data.jobs.find(j => j.id === body.jobId);
					const statusMessages: string[] = job?.statuses ? [...job.statuses] : [];
					return statusMessages[statusMessages.length - 1] || DEFAULT_STATUS_MSG;
				},
				interval: 10000,
				onStatusChange,
				defaultStatusMessage: DEFAULT_STATUS_MSG,
			});
			response = {
				success: pollResult.success,
				result: pollResult.data,
			};
		}

		const job = response.result?.jobs.find(j => j.id === body.jobId);

		return {
			success: response.success,
			result: job
				? {
						prompts: job.prompts,
						generator: { id: job.generatorId, jobId: job.id },
					}
				: null,
		};
	},
	onError: ({ error }) => {
		handleAPIv2Error(error);
	},
	alertDuration: 15_000,
});
export const patchAIStoryGenerator = createThunkAction<
	unknown,
	{ storyId: string; generatorId: string; jobId: string }
>({
	payloadCreator: async ({ storyId, jobId, generatorId }, { getState }) => {
		const organizationId = selectActiveOrganizationId(getState());
		await patchAIStory.params({ organizationId, generatorId }).send({ storyId, jobId });
		return { success: true };
	},
	onError: ({ error }) => {
		handleAPIv2Error(error);
	},
});
export const generateCardContent = createThunkAction<T.PromptsResponseList, T.GenerateCardContentParams>({
	type: GET_GENERATED_CARD_CONTENT,
	payloadCreator: async ({ onStatusChange, ...payload }, { getState }) => {
		const state = getState();
		const cardId = selectEditableCardId(state);
		const storyId = selectEditableStoryId(state);
		const organizationId = selectActiveOrganizationId(state);

		if (!cardId || !storyId) {
			return { success: false };
		}

		const postResponse = await postAICard.params({ storyId, cardId }).send(payload);
		const body = postResponse.body as { id: string; jobId: string };

		const pollResult = await pollWithTimeout({
			checkStatus: async () => {
				const { body: responseBody } = await getAIStory
					.params({ organizationId, generatorId: body.id })
					.send({ jobId: body.jobId });
				return responseBody as T.GenerateStoryContentResponse;
			},
			shouldContinue: data => {
				const job = data.jobs.find(j => j.id === body.jobId);
				return job?.status !== T.GenerateStoryStatus.completed && job?.status !== 'failed';
			},
			isFailed: data => {
				const job = data.jobs.find(j => j.id === body.jobId);
				const statusMessages: string[] = job?.statuses ? [...job.statuses] : [];
				const currentStatus = statusMessages[statusMessages.length - 1];
				const failedStatusesLen = statusMessages.filter(m => m === FAILED_STATUS_MSG).length;
				return failedStatusesLen % 2 === 0 && currentStatus === FAILED_STATUS_MSG;
			},
			getStatusMessage: data => {
				const job = data.jobs.find(j => j.id === body.jobId);
				const statusMessages: string[] = job?.statuses ? [...job.statuses] : [];
				return statusMessages[statusMessages.length - 1] || DEFAULT_STATUS_MSG;
			},
			interval: 5000,
			onStatusChange,
			defaultStatusMessage: DEFAULT_STATUS_MSG,
		});
		const job = pollResult.data?.jobs.find(j => j.id === body.jobId);

		return {
			success: pollResult.success,
			result: job?.prompts,
		};
	},
	onError: ({ error }) => {
		handleAPIv2Error(error);
	},
	alertDuration: 15_000,
});
